import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { LOCAL_AUTH, getCurrentUser, getIdToken, signIn as cognitoSignIn, completeNewPassword, signOut as cognitoSignOut, submitMfaCode } from "./cognito";
import { applyCodeLists } from "../../../shared/codes";
import { api, setDevUser, type Me } from "../lib/api";
import { microsoftSignOutUrl } from "./microsoft";

interface AuthState {
  me: Me | null;
  loading: boolean;
  /** Error from loading /me (e.g. the API is unreachable), not a sign-in failure. */
  loadError: string | null;
  /** Resolves to an error message when /me couldn't load, otherwise null. */
  refresh: () => Promise<string | null>;
  signIn: (email: string, password: string) => Promise<void>;
  completeNewPassword: (email: string, oldPassword: string, newPassword: string) => Promise<void>;
  /** Second step of a password sign-in: the authenticator code (2026-10-27). */
  submitMfa: (code: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);
const SIGNED_OUT_KEY = "docvault.signedOut";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<string | null> => {
    setLoadError(null);
    try {
      if (LOCAL_AUTH) {
        // Local dev: the API's sign-in bypass decides who we are (x-dev-user).
        if (sessionStorage.getItem(SIGNED_OUT_KEY)) { setMe(null); return null; }
      } else if (!getCurrentUser() || !(await getIdToken())) {
        setMe(null);
        return null;
      }
      const [m, codes] = await Promise.all([api.me(), api.codeLists().catch(() => null)]);
      // Admin-managed code lists (ADR-0016) replace the built-in defaults before any screen renders.
      if (codes) applyCodeLists(codes.codes);
      setMe(m);
      return null;
    } catch (e) {
      setMe(null);
      const status = (e as { status?: number }).status;
      const msg = status === 401 ? "DocVault didn't accept the sign-in." : (e as Error).message || "Couldn't reach DocVault.";
      if (status !== 401) setLoadError(msg);
      return msg;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    if (LOCAL_AUTH) {
      sessionStorage.removeItem(SIGNED_OUT_KEY);
      setDevUser(email.trim().toLowerCase());
    } else {
      await cognitoSignIn(email.trim(), password);
    }
    // Password was right but the account didn't load: say so on the sign-in
    // page instead of silently staying there (first AWS deploy, 2026-10-16).
    const err = await refresh();
    if (err) throw new Error(`Your password worked, but DocVault couldn't open your account: ${err}`);
  }, [refresh]);

  const finishNewPassword = useCallback(async (email: string, oldPassword: string, newPassword: string) => {
    await completeNewPassword(email.trim(), oldPassword, newPassword);
    const err = await refresh();
    if (err) throw new Error(`Your new password is saved, but DocVault couldn't open your account: ${err}. Use the new password next time.`);
  }, [refresh]);

  const submitMfa = useCallback(async (code: string) => {
    await submitMfaCode(code.replace(/\s+/g, ""));
    const err = await refresh();
    if (err) throw new Error(`Your code worked, but DocVault couldn't open your account: ${err}`);
  }, [refresh]);

  const signOut = useCallback(() => {
    if (LOCAL_AUTH) sessionStorage.setItem(SIGNED_OUT_KEY, "1");
    else {
      cognitoSignOut();
      // Signed in with Microsoft: also end the Cognito web session, so the next
      // Microsoft sign-in can't slip straight back in on a shared computer.
      const out = microsoftSignOutUrl();
      if (out) { window.location.assign(out); return; }
    }
    setMe(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ me, loading, loadError, refresh, signIn, completeNewPassword: finishNewPassword, submitMfa, signOut }),
    [me, loading, loadError, refresh, signIn, finishNewPassword, submitMfa, signOut]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
